import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Box, Button, Center, SimpleGrid, Spinner, Text } from '@chakra-ui/react';
import { RequestGridCard } from '@src/components/sco/Requests/RequestGridCard';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { GroupRequest, Pagination } from '@src/graphql-types';
import { gql } from '@src/lib/gql';
import React, { ReactNode, useEffect, useState } from 'react';
type RequestsGridProps = {
  variables?: {
    compensationTypeKeys?: string[];
    groupRequestTypeKeys?: string[];
    publishStatusKeys?: string[];
    requestingUserId?: string;
    tagTitles?: string[];
  };
  allowLoadMore?: boolean;
  pagination?: {
    page?: number;
    pageSize?: number;
    order?: string[][];
  };
  renderEmpty?: () => ReactNode;
};
export const RequestsGrid: React.FC<RequestsGridProps> = props => {
  const $ = _c(40);
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const [page, setPage] = useState(props.pagination?.page || 1);
  const [pageSize] = useState(props.pagination?.pageSize || 24);
  let t0;
  if ($[0] !== props.pagination?.order) {
    t0 = props.pagination?.order || [["createdAt", "desc"]];
    $[0] = props.pagination?.order;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [order] = useState(t0);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = gql`
      query (
        $groupRequestTypeKeys: [String]
        $tagTitles: [String]
        $compensationTypeKeys: [String]
        $requestingUserId: String
        $publishStatusKeys: [String]
        $page: Int
        $pageSize: Int
        $order: [[String]]
      ) {
        groupRequests(
          publishStatusKeys: $publishStatusKeys
          groupRequestTypeKeys: $groupRequestTypeKeys
          tagTitles: $tagTitles
          compensationTypeKeys: $compensationTypeKeys
          requestingUserId: $requestingUserId
          page: $page
          pageSize: $pageSize
          order: $order
        ) {
          id
          items {
            id
            groupRequestId
          }
          pagination {
            itemCount
            hasNextPage
          }
        }
      }
    `;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = props.pagination?.page || 1;
  let t3;
  if ($[3] !== order || $[4] !== pageSize || $[5] !== props.variables || $[6] !== t2) {
    t3 = {
      fetchPolicy: "network-only",
      ssr: false,
      variables: {
        ...props.variables,
        page: t2,
        pageSize,
        order
      }
    };
    $[3] = order;
    $[4] = pageSize;
    $[5] = props.variables;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const query = useQuery(t1, t3);
  let t4;
  if ($[8] !== order || $[9] !== page || $[10] !== pageSize || $[11] !== props.variables || $[12] !== query) {
    t4 = () => {
      query.fetchMore({
        variables: {
          ...props.variables,
          page,
          pageSize,
          order
        }
      });
    };
    $[8] = order;
    $[9] = page;
    $[10] = pageSize;
    $[11] = props.variables;
    $[12] = query;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== page) {
    t5 = [page];
    $[14] = page;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  useEffect(t4, t5);
  if (query.loading) {
    let t6;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Center padding={4} width="full"><Spinner /></Center>;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    return t6;
  }
  if (query.error) {
    let t6;
    if ($[17] !== query.error) {
      t6 = query.error.toString();
      $[17] = query.error;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t7;
    if ($[19] !== t6) {
      t7 = <Center padding={4}><Text>{t6}</Text></Center>;
      $[19] = t6;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    return t7;
  }
  const groupRequests = query.data?.groupRequests?.items;
  const pagination = query.data?.groupRequests?.pagination;
  let t6;
  if ($[21] !== groupRequests.length || $[22] !== props.renderEmpty) {
    t6 = groupRequests.length === 0 && <>{props.renderEmpty ? props.renderEmpty() : <Center width="full"><Text>No requests yet.</Text></Center>}</>;
    $[21] = groupRequests.length;
    $[22] = props.renderEmpty;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== getBreakpointValue) {
    t7 = getBreakpointValue([1, 1, 2, 3]);
    $[24] = getBreakpointValue;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== groupRequests) {
    t8 = groupRequests.map(_temp);
    $[26] = groupRequests;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== t7 || $[29] !== t8) {
    t9 = <SimpleGrid columns={t7} gap={4}>{t8}</SimpleGrid>;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== page || $[32] !== pagination?.hasNextPage || $[33] !== props.allowLoadMore || $[34] !== query.loading) {
    t10 = props.allowLoadMore && <Center paddingY={8}><Button disabled={!pagination?.hasNextPage} loading={query.loading} onClick={() => setPage(page + 1)}>{!pagination?.hasNextPage ? "All Loaded" : "Load More"}</Button></Center>;
    $[31] = page;
    $[32] = pagination?.hasNextPage;
    $[33] = props.allowLoadMore;
    $[34] = query.loading;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== t10 || $[37] !== t6 || $[38] !== t9) {
    t11 = <Box>{t6}{t9}{t10}</Box>;
    $[36] = t10;
    $[37] = t6;
    $[38] = t9;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  return t11;
};
function _temp(groupRequest) {
  return <RequestGridCard key={groupRequest.id} groupRequestId={groupRequest.groupRequestId} />;
}